import { useState, useEffect, useCallback, useMemo } from "react";
import { Loader2, Plus, History, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { RenataChat } from "./RenataChat";
import type { RenataConversation, RenataMessage } from "@/lib/renata/types";
import type { RenataWorkspaceFocus } from "@/lib/renata/workspace-focus";
import { cn } from "@/lib/utils";
import { clientRequestHeaders } from "@/lib/sm-api";

interface RenataPanelProps {
  tenantId: string;
  currentRoute?: string;
  compact?: boolean;
  /** Reanudar esta conversación si existe (FAB / deep link). */
  initialConversationId?: string | null;
  /** Tarea pre-seleccionada (?task= deep link). */
  initialTaskId?: string | null;
  onConversationChange?: (id: string | null) => void;
  /** Producto/pedido abierto en el editor (FAB + Approval). */
  workspaceFocus?: RenataWorkspaceFocus | null;
}

function buildChatContext(currentRoute?: string, focus?: RenataWorkspaceFocus | null) {
  const entityKind = focus?.entity;
  const entityId = focus?.entityId ?? null;
  const entity =
    entityKind && entityId
      ? `${entityKind}:${entityId}`
      : entityKind || undefined;
  return {
    route: focus?.route ?? currentRoute,
    entity,
    entityId,
    entityName: focus?.entityName ?? null,
    selection: focus?.selection,
    draft: focus?.draft,
    field: focus?.field,
    intent: focus?.intent,
  };
}

function conversationLabel(c: RenataConversation): string {
  if (c.title?.trim()) return c.title.trim();
  const d = new Date(c.updated_at || c.created_at);
  return d.toLocaleString("es-AR", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

function setConversationSearchParam(id: string | null) {
  if (typeof window === "undefined") return;
  // Solo tocar ?c= en la página full de Renata; el FAB no contamina otras rutas
  if (!window.location.pathname.startsWith("/app/renata")) return;
  const url = new URL(window.location.href);
  if (id) url.searchParams.set("c", id);
  else url.searchParams.delete("c");
  window.history.replaceState({}, "", url.toString());
}

export function RenataPanel({
  tenantId,
  currentRoute,
  compact = false,
  initialConversationId = null,
  initialTaskId = null,
  onConversationChange,
  workspaceFocus = null,
}: RenataPanelProps) {
  const [conversation, setConversation] = useState<RenataConversation | null>(null);
  const [messages, setMessages] = useState<RenataMessage[]>([]);
  const [history, setHistory] = useState<RenataConversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const chatContext = useMemo(
    () => buildChatContext(currentRoute, workspaceFocus),
    [currentRoute, workspaceFocus],
  );

  const headers = useCallback(
    () =>
      ({
        ...clientRequestHeaders(tenantId),
        "Content-Type": "application/json",
      }) as HeadersInit,
    [tenantId],
  );

  const notifyConversation = useCallback(
    (id: string | null) => {
      onConversationChange?.(id);
      setConversationSearchParam(id);
    },
    [onConversationChange],
  );

  const loadHistory = useCallback(async () => {
    try {
      const res = await fetch("/api/renata/conversations", {
        credentials: "include",
        headers: clientRequestHeaders(tenantId),
      });
      if (!res.ok) return [] as RenataConversation[];
      const data = (await res.json()) as { conversations: RenataConversation[] };
      const list = data.conversations ?? [];
      setHistory(list);
      return list;
    } catch {
      return [] as RenataConversation[];
    }
  }, [tenantId]);

  const loadConversation = useCallback(
    async (id: string) => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(`/api/renata/conversations?id=${encodeURIComponent(id)}`, {
          credentials: "include",
          headers: clientRequestHeaders(tenantId),
        });
        if (!res.ok) throw new Error("not found");
        const data = (await res.json()) as {
          conversation: RenataConversation & { messages?: RenataMessage[] };
        };
        setConversation(data.conversation);
        setMessages(data.conversation.messages ?? []);
        notifyConversation(data.conversation.id);
      } catch {
        setConversation(null);
        setMessages([]);
        setError("No se pudo cargar la conversación.");
      } finally {
        setLoading(false);
      }
    },
    [tenantId, notifyConversation],
  );

  const createConversation = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch("/api/renata/conversations", {
        method: "POST",
        credentials: "include",
        headers: headers(),
        body: JSON.stringify({
          surface: currentRoute,
          context: chatContext,
        }),
      });
      const data = (await res.json()) as { conversation?: RenataConversation; error?: string };
      if (!res.ok || !data.conversation) {
        setError(data.error ?? `No se pudo iniciar (HTTP ${res.status})`);
        setConversation(null);
        setMessages([]);
        return;
      }
      setConversation(data.conversation);
      setMessages([]);
      notifyConversation(data.conversation.id);
      void loadHistory();
    } catch {
      setError("No se pudo iniciar la conversación.");
      setConversation(null);
      setMessages([]);
    } finally {
      setLoading(false);
    }
  }, [chatContext, currentRoute, headers, loadHistory, notifyConversation]);

  useEffect(() => {
    let cancelled = false;
    async function init() {
      setLoading(true);
      setError(null);
      const list = await loadHistory();
      if (cancelled) return;

      const cParam =
        typeof window !== "undefined" && window.location.pathname.startsWith("/app/renata")
          ? new URLSearchParams(window.location.search).get("c")
          : null;

      const preferred = cParam || initialConversationId || list[0]?.id || null;

      if (preferred) {
        await loadConversation(preferred);
        return;
      }

      await createConversation();
    }
    void init();
    return () => {
      cancelled = true;
    };
    // Solo remount al cambiar tenant
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  if (loading) {
    return (
      <div className="flex h-full min-h-0 items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!conversation) {
    return (
      <div className="flex h-full min-h-0 flex-col items-center justify-center gap-3 p-4 text-center">
        <p className="text-sm text-muted-foreground">{error ?? "No se pudo iniciar la conversación."}</p>
        <Button size="sm" onClick={() => void createConversation()}>
          Reintentar
        </Button>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="relative flex shrink-0 items-center justify-between gap-2 border-b px-2 py-1.5">
        <div className="min-w-0 flex-1">
          <Button
            size="sm"
            variant="ghost"
            className="h-7 max-w-full gap-1 px-2 text-xs"
            onClick={() => setHistoryOpen((v) => !v)}
          >
            <History className="h-3 w-3 shrink-0" />
            <span className="truncate">{conversationLabel(conversation)}</span>
            <ChevronDown className={cn("h-3 w-3 shrink-0 transition", historyOpen && "rotate-180")} />
          </Button>
        </div>
        <Button
          size="sm"
          variant="ghost"
          className="h-7 shrink-0 gap-1 text-xs"
          onClick={() => {
            setHistoryOpen(false);
            void createConversation();
          }}
          title="Nueva conversación"
        >
          <Plus className="h-3 w-3" />
          {!compact && "Nueva"}
        </Button>

        {historyOpen && (
          <>
            <button
              type="button"
              className="fixed inset-0 z-10"
              aria-label="Cerrar historial"
              onClick={() => setHistoryOpen(false)}
            />
            <div className="absolute left-2 right-2 top-full z-20 mt-1 max-h-56 overflow-y-auto rounded-lg border bg-background shadow-lg">
              {history.length === 0 ? (
                <p className="px-3 py-2 text-xs text-muted-foreground">Sin conversaciones previas</p>
              ) : (
                history.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    className={cn(
                      "flex w-full items-center px-3 py-2 text-left text-xs hover:bg-muted/60",
                      c.id === conversation.id && "bg-muted font-medium",
                    )}
                    onClick={() => {
                      setHistoryOpen(false);
                      void loadConversation(c.id);
                    }}
                  >
                    <span className="truncate">{conversationLabel(c)}</span>
                  </button>
                ))
              )}
            </div>
          </>
        )}
      </div>

      <div className="min-h-0 flex-1 overflow-hidden">
        <RenataChat
          key={conversation.id}
          tenantId={tenantId}
          conversation={conversation}
          initialMessages={messages}
          context={chatContext}
          initialTaskId={initialTaskId}
          className="h-full min-h-0"
        />
      </div>
    </div>
  );
}
